import { invoicePdfQueue } from './invoicePdfUpload';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  InvoiceIssueRequest,
  InvoiceReferences,
  InvoiceResponse,
  InvoiceSnapshot,
} from '../../api/types';
import {
  issueInvoice,
  mapPaymentError,
  previewExplicitInvoice,
} from '../../api/endpoints/payments';
import { GST_STATE_NAMES } from './gstStates';
import { invoiceMoney } from './invoiceCopyText';
import styles from '../payments/Payments.module.css';
import { Button } from '../../components/ui/Button';

/** Explicit invoice-specific staff attestation; an absent GSTIN never selects it. */
export function ExemptInvoiceForm({
  shipmentId,
  clientRequestId,
  references,
  onIssued,
  onBusyChange,
  onLockedChange,
}: {
  shipmentId: number;
  clientRequestId: string;
  references: InvoiceReferences;
  onBusyChange: (busy: boolean) => void;
  onLockedChange?: (locked: boolean) => void;
  onIssued: (invoice: InvoiceResponse) => void | Promise<void>;
}) {
  const { t } = useTranslation();
  const [requestId, setRequestId] = useState(clientRequestId);
  const [reason, setReason] = useState(''),
    [reference, setReference] = useState(''),
    [state, setState] = useState('');
  const [confirmed, setConfirmed] = useState(false),
    [preview, setPreview] = useState<InvoiceSnapshot | null>(null);
  const [busy, setBusy] = useState(false),
    [loading, setLoading] = useState(false),
    [error, setError] = useState<string | null>(null);
  const inFlight = useRef(false),
    frozen = useRef<InvoiceIssueRequest | null>(null);
  const valid = Boolean(
    reason.trim() &&
    reason.trim().length <= 500 &&
    reference.trim().length <= 200 &&
    state &&
    confirmed,
  );
  const request: InvoiceIssueRequest = {
    clientRequestId: requestId,
    taxTreatment: 'EXEMPT',
    exemption: {
      reason: reason.trim(),
      confirmed,
      recipientStateCode: state,
      reference: reference.trim() || null,
    },
    references,
  };
  useEffect(() => {
    setPreview(null);
    setError(null);
    setLoading(false);
    if (!valid) return;
    let active = true;
    setLoading(true);
    const timer = setTimeout(() => {
      void previewExplicitInvoice(shipmentId, {
        clientRequestId: requestId,
        taxTreatment: 'EXEMPT',
        exemption: {
          reason: reason.trim(),
          confirmed,
          recipientStateCode: state,
          reference: reference.trim() || null,
        },
        references,
      })
        .then((value) => {
          if (active) setPreview(value);
        })
        .catch((cause) => {
          if (active) setError(mapPaymentError(cause));
        })
        .finally(() => {
          if (active) setLoading(false);
        });
    }, 300);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [shipmentId, requestId, reason, reference, state, confirmed, references, valid]);
  const locked = busy || Boolean(frozen.current);
  useEffect(() => {
    onLockedChange?.(locked);
  }, [locked, onLockedChange]);
  return (
    <section className={styles.card} aria-label={t('invoices.exemptInvoiceForm.sectionLabel')}>
      <p>{t('invoices.exemptInvoiceForm.intro')}</p>
      <fieldset disabled={locked}>
        <legend>{t('invoices.exemptInvoiceForm.legend')}</legend>
        <label className={styles.field}>
          {t('invoices.exemptInvoiceForm.reason')}
          <textarea
            aria-label={t('invoices.exemptInvoiceForm.reason')}
            maxLength={500}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
        </label>
        <label className={styles.field}>
          {t('invoices.exemptInvoiceForm.referenceField')}
          <input
            aria-label={t('invoices.exemptInvoiceForm.referenceLabel')}
            maxLength={200}
            value={reference}
            onChange={(event) => setReference(event.target.value)}
          />
        </label>
        <label className={styles.field}>
          {t('invoices.exemptInvoiceForm.recipientState')}
          <select
            aria-label={t('invoices.exemptInvoiceForm.recipientState')}
            value={state}
            onChange={(event) => setState(event.target.value)}
          >
            <option value="">{t('invoices.exemptInvoiceForm.selectState')}</option>
            {Object.entries(GST_STATE_NAMES)
              .filter(([code]) => /^(0[1-9]|[12][0-9]|3[0-8])$/.test(code))
              .map(([code, name]) => (
                <option key={code} value={code}>
                  {code} · {name}
                </option>
              ))}
          </select>
        </label>
        <label>
          <input
            type="checkbox"
            checked={confirmed}
            onChange={(event) => setConfirmed(event.target.checked)}
          />
          {t('invoices.exemptInvoiceForm.confirm')}
        </label>
      </fieldset>
      {loading && <p role="status">{t('invoices.exemptInvoiceForm.updating')}</p>}
      {preview && (
        <div>
          <h3>{t('invoices.exemptInvoiceForm.previewTitle')}</h3>
          <p>
            {t('invoices.exemptInvoiceForm.buyer')} {preview.buyer.companyName} ·{' '}
            {preview.buyer.billingAddress}
          </p>
          <p>
            {t('invoices.exemptInvoiceForm.supplyValue', {
              amount: invoiceMoney(preview.tax.taxableAmount),
            })}
          </p>
          <strong>
            {t('invoices.exemptInvoiceForm.grandTotal', {
              amount: invoiceMoney(preview.tax.grandTotal),
            })}
          </strong>
          <p>{t('invoices.exemptInvoiceForm.frozenNote')}</p>
        </div>
      )}
      {error && <p role="alert">{error}</p>}
      <Button
        variant="primary"
        size="sm"
        disabled={!valid || !preview || loading || busy}
        onClick={async () => {
          if (inFlight.current || !valid || !preview || loading) return;
          inFlight.current = true;
          setBusy(true);
          onBusyChange(true);
          setError(null);
          frozen.current ??= request;
          try {
            const saved = await issueInvoice(shipmentId, frozen.current);
            void invoicePdfQueue.ensure(saved, 'ORIGINAL');
            await onIssued(saved);
          } catch (cause) {
            setError(mapPaymentError(cause));
            const failure = cause as { status?: number; code?: string };
            if (
              failure.status != null &&
              failure.status >= 400 &&
              failure.status < 500 &&
              failure.status !== 408 &&
              failure.code !== 'IDEMPOTENCY_RETRY'
            ) {
              frozen.current = null;
              setRequestId(crypto.randomUUID());
            }
          } finally {
            inFlight.current = false;
            setBusy(false);
            onBusyChange(false);
          }
        }}
      >
        {busy ? 'Issuing…' : frozen.current ? 'Retry exempt issue' : 'Issue Bill of Supply'}
      </Button>
    </section>
  );
}
